import { FC, memo, useState } from "react";

import {
  Building,
  CheckmarkFilled,
  DotMark,
  Email,
  Group,
  OverflowMenuVertical,
  Time,
  TrashCan,
} from "@carbon/icons-react";
import { Link } from "react-router-dom";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { GroupUserStatusEnum } from "@/features/groups/constants/group.constant";
import { GroupPathsEnum } from "@/features/groups/constants/group.paths";
import { formatDistance } from "@/utils/date";
import { extractUsername } from "@/utils/extractUsername";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { IGroupInvitation } from "../../../types/group.type";
import AcceptGroupModal from "./AcceptGroupModal";
import AcceptOrgModal from "./AcceptOrgModal";
import CancelConfirmModal from "./CancelConfirmModal";
import RejectOrgModal from "./RejectOrgModal";

interface GroupCardProps {
  group: IGroupInvitation;
}

const GroupCardInvite: FC<GroupCardProps> = ({ group }) => {
  const [isSuccess, setIsSuccess] = useState(false);

  const isRootGroup = group.rootId === group.groupId;
  const isPending =
    group.status === GroupUserStatusEnum.WAIT_ORGANIZATION_APPROVE;

  return (
    <>
      <div className="group relative bg-white rounded-2xl border border-secondary-200 hover:border-primary-300 hover:shadow-xl transition-all duration-300 ease-out overflow-hidden">
        {/* Cover Image with Overlay */}
        <div className="relative h-48 overflow-hidden bg-gradient-to-br from-primary-50 to-primary-100">
          {group.photoUrl ? (
            <img
              className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
              src={group.photoUrl}
              alt={group.name}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary-100 via-primary-50 to-secondary-50">
              <div className="text-center">
                {isRootGroup ? (
                  <Building
                    size={48}
                    className="text-primary-400 mx-auto mb-2"
                  />
                ) : (
                  <Group
                    size={48}
                    className="text-primary-400 mx-auto mb-2"
                  />
                )}
                <span className="text-sm font-medium text-primary-600">
                  {isRootGroup ? "Tổ chức" : "Nhóm"}
                </span>
              </div>
            </div>
          )}

          {/* Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/10 via-transparent to-transparent"></div>

          {/* Group Type Badge */}
          <div className="absolute top-4 left-4">
            <Badge
              className={`
              px-2 py-1 text-xs font-medium rounded-full border-0 backdrop-blur-sm
              ${
                isRootGroup
                  ? "bg-white/90 text-primary-700"
                  : "bg-white/90 text-secondary-700"
              }
            `}
            >
              {isRootGroup ? (
                <>
                  <Building
                    size={12}
                    className="mr-1"
                  />
                  Tổ chức
                </>
              ) : (
                <>
                  <Group
                    size={12}
                    className="mr-1"
                  />
                  Nhóm
                </>
              )}
            </Badge>
          </div>

          {/* Status Badge */}
          <div className="absolute top-4 right-4">
            <Badge
              className={`
              px-2 py-1 text-xs font-medium rounded-full border-0 backdrop-blur-sm
              ${
                isPending
                  ? "bg-amber-100/90 text-amber-700"
                  : "bg-blue-100/90 text-blue-700"
              }
            `}
            >
              <Email
                size={12}
                className="mr-1"
              />
              {isPending ? "Đang xử lý" : "Lời mời"}
            </Badge>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Header with Category */}
          <div className="mb-4">
            {/* Parent Organization */}
            {group.rootName && (
              <div className="flex items-center text-xs font-medium text-secondary-500 uppercase tracking-wider mb-1">
                <span>Tổ chức</span>
                <DotMark
                  size={4}
                  className="mx-2"
                />
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Link
                        className="text-primary-600 hover:text-primary-700 transition-colors duration-200 normal-case max-w-[180px] truncate"
                        to={GroupPathsEnum.GROUP_DETAIL.replace(
                          ":id",
                          String(group.rootId)
                        )}
                      >
                        {group.rootName}
                      </Link>
                    </TooltipTrigger>
                    <TooltipContent>
                      <span className="text-xs">{group.rootName}</span>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            )}

            {/* Group Title */}
            <h3 className="mb-4">
              <Link
                className="text-xl font-bold text-secondary-900 hover:text-primary-600 transition-colors duration-200 line-clamp-2 leading-tight"
                to={GroupPathsEnum.GROUP_DETAIL.replace(
                  ":id",
                  String(group.groupId)
                )}
              >
                {group.name}
              </Link>
            </h3>
          </div>

          {/* Sender Info */}
          {group.sender && (
            <div className="flex items-center justify-between mb-6 p-3 bg-secondary-50 rounded-xl">
              <div className="flex items-center space-x-3">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Link
                        className="flex-shrink-0"
                        to="#"
                      >
                        <Avatar className="h-10 w-10">
                          <AvatarImage src={group.sender.photoUrl || ""} />
                          <AvatarFallback>
                            {group.sender.name?.charAt(0) ||
                              extractUsername(group.sender.email || "")}
                          </AvatarFallback>
                        </Avatar>
                      </Link>
                    </TooltipTrigger>
                    <TooltipContent>
                      <span className="text-xs">
                        {group.sender.name || group.sender.email}
                      </span>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>

                <div>
                  <p className="text-sm font-medium text-secondary-900">
                    {group.sender.name ||
                      extractUsername(group.sender.email || "")}
                  </p>
                  <div className="flex items-center text-xs text-secondary-500">
                    <Time
                      size={12}
                      className="mr-1"
                    />
                    Đã gửi {formatDistance(group.invitedAt)}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="space-y-3">
            {isPending ? (
              <div className="flex items-center gap-3">
                <Button
                  className="flex-1 bg-amber-100 hover:bg-amber-200 font-medium cursor-not-allowed"
                  disabled
                >
                  Đang xử lý...
                </Button>

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      className="w-12 h-12 rounded-xl border border-secondary-200 hover:border-secondary-300 hover:bg-secondary-50 transition-all duration-200"
                      variant="ghost"
                      size="sm"
                    >
                      <OverflowMenuVertical
                        size={18}
                        className="text-secondary-600"
                      />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    className="w-52"
                    align="end"
                  >
                    <CancelConfirmModal
                      isDetail={false}
                      groupInfo={group}
                      button={
                        <Button
                          variant="ghost"
                          className="w-full justify-start text-red-600 hover:bg-red-50 py-3"
                        >
                          <TrashCan
                            size={16}
                            className="mr-2"
                          />
                          Hủy tham gia
                        </Button>
                      }
                    />
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            ) : (
              <div className="flex items-center gap-3">
                {isRootGroup ? (
                  <AcceptOrgModal
                    inviteId={group.id}
                    groupId={group.groupId}
                    groupName={group.name}
                    isDetail={false}
                    rootGroupId={group.rootId}
                    groupLock={group.groupLock}
                    button={
                      <Button className="flex-1 bg-green-600 text-white font-medium transition-all duration-200 shadow-sm hover:shadow-md transform hover:-translate-y-0.5 w-full">
                        Chấp nhận
                      </Button>
                    }
                  />
                ) : (
                  <AcceptGroupModal
                    rootGroupName={group.rootName}
                    inviteId={group.id}
                    groupId={group.groupId}
                    groupName={group.name}
                    isDetail={false}
                    rootGroupId={group.rootId}
                    groupLock={group.groupLock}
                    button={
                      <Button className="flex-1 bg-green-600 text-white font-medium transition-all duration-200 shadow-sm hover:shadow-md transform hover:-translate-y-0.5 w-full">
                        Chấp nhận
                      </Button>
                    }
                  />
                )}

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      className="w-12 h-12 rounded-xl border border-secondary-200 hover:border-secondary-300 hover:bg-secondary-50 transition-all duration-200 hover:-translate-y-0.5"
                      variant="ghost"
                      size="sm"
                    >
                      <OverflowMenuVertical
                        size={18}
                        className="text-secondary-600"
                      />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    className="w-52"
                    align="end"
                  >
                    <RejectOrgModal
                      groupId={group.groupId}
                      groupName={group.name}
                      inviteId={group.id}
                      rootGroupId={group.rootId}
                      groupLock={group.groupLock}
                      isDetail={false}
                      button={
                        <Button
                          variant="ghost"
                          className="w-full justify-start text-red-600 hover:bg-red-50 py-3"
                        >
                          <TrashCan
                            size={16}
                            className="mr-2"
                          />
                          Từ chối lời mời
                        </Button>
                      }
                    />
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Success Modal */}
      <Dialog
        open={isSuccess}
        onOpenChange={setIsSuccess}
      >
        <DialogContent className="max-w-[480px] rounded-2xl">
          <DialogHeader className="text-center space-y-4">
            <div className="mx-auto w-16 h-16 bg-gradient-to-r from-green-100 to-emerald-100 rounded-full flex items-center justify-center">
              <CheckmarkFilled
                size={32}
                className="text-green-600"
              />
            </div>
            <DialogTitle className="text-2xl font-bold text-secondary-900">
              Chào mừng bạn! 🎉
            </DialogTitle>
            <DialogDescription className="text-secondary-600 leading-relaxed">
              Bạn đã trở thành thành viên của{" "}
              <span className="font-semibold text-secondary-900">
                "{group?.name}"
              </span>
              .
              <br />
              Giờ đây bạn có thể tham gia các hoạt động và kết nối với các thành
              viên khác.
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-col space-y-3 pt-4">
            <Link
              className="w-full bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 text-white font-medium py-3 px-6 rounded-xl text-center transition-all duration-200 shadow-sm hover:shadow-md"
              to={GroupPathsEnum.GROUP_DETAIL.replace(
                ":id",
                String(group.groupId)
              )}
            >
              Khám phá ngay
            </Link>

            <Button
              variant="ghost"
              onClick={() => setIsSuccess(false)}
              className="w-full text-secondary-600 hover:text-secondary-900"
            >
              Để sau
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default memo(GroupCardInvite);
